import React, { useState, useEffect } from 'react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  Users, 
  MessageSquare, 
  Bot, 
  TrendingUp, 
  Zap,
  Activity,
  ShieldCheck,
  Globe,
  Sparkles,
  LayoutDashboard,
  ShoppingCart,
  Ticket
} from 'lucide-react';
import { motion } from 'framer-motion';
import { getDashboardStats, getBots, getTickets } from '@/lib/api';
import { toast } from 'sonner';

// Mock data for premium visualization extensions
const categoryData = [
  { name: 'Hielo Seco', value: 400 },
  { name: 'Cubos 5kg', value: 300 },
  { name: 'Frappe', value: 300 },
  { name: 'Barra', value: 200 },
];

const COLORS = ['#10b981', '#8b5cf6', '#ec4899', '#f59e0b'];

const DashboardPage = () => {
    const [stats, setStats] = useState(null);
    const [bots, setBots] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [statsRes, botsRes] = await Promise.all([
                getDashboardStats(),
                getBots()
            ]);
            
            // Backend returns chart_data directly
            setStats(statsRes.data);
            setBots(botsRes.data);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            toast.error('Error al cargar los datos del dashboard');
        } finally {
            setLoading(false);
        }
    };

    // Use backend data or fallback to empty for safety
    const chartData = stats?.chart_data || [];
    const categoryData = [
        { name: 'Retail / Tiendas', value: 35 },
        { name: 'Servicios Profesionales', value: 25 },
        { name: 'Salud / Clínicas', value: 20 },
        { name: 'Gastronomía', value: 20 },
    ];

    const COLORS = ['#10b981', '#8b5cf6', '#ec4899', '#f59e0b'];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="relative">
                    <div className="w-16 h-16 rounded-full border-4 border-emerald-500/20 border-t-emerald-500 animate-spin" />
                    <div className="absolute inset-4 blur-xl bg-emerald-500/20 animate-pulse" />
                </div>
            </div>
        );
    }

    const statCards = [
        { 
            title: 'Negocios SaaS', 
            value: stats?.total_tenants || '0', 
            change: '+15%', 
            trend: 'up', 
            icon: Users,
            color: 'emerald'
        },
        { 
            title: 'Bots Operando', 
            value: `${stats?.active_bots || '0'}/${stats?.total_bots || '0'}`, 
            change: '+2', 
            trend: 'up', 
            icon: Bot,
            color: 'purple'
        },
        { 
            title: 'Mensajes Totales', 
            value: stats?.total_conversations || '0', 
            change: '+22.4%', 
            trend: 'up', 
            icon: MessageSquare,
            color: 'pink'
        },
        { 
            title: 'Tickets Creados', 
            value: stats?.total_tickets || '0', 
            change: '+5%', 
            trend: 'up', 
            icon: Ticket,
            color: 'orange'
        }
    ];

    const colorStyles = {
        emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-500', glow: 'bg-emerald-500/5', hover: 'group-hover:bg-emerald-500/10' },
        purple: { bg: 'bg-purple-500/10', text: 'text-purple-500', glow: 'bg-purple-500/5', hover: 'group-hover:bg-purple-500/10' },
        pink: { bg: 'bg-pink-500/10', text: 'text-pink-500', glow: 'bg-pink-500/5', hover: 'group-hover:bg-pink-500/10' },
        orange: { bg: 'bg-orange-500/10', text: 'text-orange-500', glow: 'bg-orange-500/5', hover: 'group-hover:bg-orange-500/10' }
    };

    return (
        <div className="space-y-8 pb-10">
            {/* Hero Section */}
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative overflow-hidden p-6 rounded-[2rem] bg-gradient-to-br from-emerald-600/20 to-teal-900/40 border border-emerald-500/20 shadow-2xl shadow-emerald-900/20"
            >
                <div className="absolute -top-20 -right-20 w-48 h-48 bg-emerald-500/10 blur-[60px] rounded-full" />
                <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-teal-500/10 blur-[60px] rounded-full" />
                
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-emerald-400 font-bold text-xs uppercase tracking-[0.2em]">
                            <LayoutDashboard className="w-4 h-4" />
                            <span>Dashboard Maestro Administrador</span>
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold text-white font-outfit">
                            Flujo de Operación <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-300">Global</span>
                        </h1>
                        <p className="text-slate-400 max-w-md"> Tu plataforma SaaS está gestionando <span className="text-emerald-400 font-bold">{stats?.total_tenants} tenants</span> con éxito hoy. </p>
                    </div>

                    <div className="flex items-center gap-4 bg-[#0F172A]/40 backdrop-blur-md p-4 rounded-3xl border border-white/5">
                        <div className="p-3 bg-blue-500/10 rounded-2xl">
                            <Zap className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Ahorro de Tiempo</p>
                            <h4 className="text-xl font-bold text-white leading-tight">{stats?.hours_saved} Horas</h4>
                            <p className="text-[9px] text-emerald-400 font-medium">ROI Total Acumulado</p>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((stat, index) => (
                    <motion.div
                        key={stat.title}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        className="group relative h-[180px] rounded-[2.5rem] bg-[#0F172A]/40 backdrop-blur-3xl border border-slate-800/50 hover:bg-[#0F172A]/60 transition-all duration-500 p-6 flex flex-col justify-between overflow-hidden cursor-default shadow-lg hover:shadow-2xl hover:shadow-blue-900/10"
                    >
                        <div className={`absolute -right-10 -bottom-10 w-24 h-24 ${colorStyles[stat.color].glow} blur-3xl ${colorStyles[stat.color].hover} transition-all duration-500`} />
                        
                        <div className="flex justify-between items-start">
                            <div className={`p-3 ${colorStyles[stat.color].bg} rounded-2xl group-hover:scale-110 transition-transform duration-500`}>
                                <stat.icon className={`w-6 h-6 ${colorStyles[stat.color].text}`} />
                            </div>
                            <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 rounded-full border border-emerald-500/20">
                                <span className="text-[10px] font-bold text-emerald-500 uppercase">{stat.change}</span>
                                <TrendingUp className="w-3 h-3 text-emerald-500" />
                            </div>
                        </div>

                        <div>
                            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">{stat.title}</p>
                            <h3 className="text-3xl font-bold text-white group-hover:text-blue-400 transition-colors duration-300">{stat.value}</h3>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Activity Chart */}
                <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="lg:col-span-2 p-8 rounded-[2.5rem] bg-[#0F172A]/40 backdrop-blur-3xl border border-slate-800/50"
                >
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h2 className="text-2xl font-bold text-white font-outfit">Volumen de Actividad Global</h2>
                            <p className="text-slate-500 text-sm">Flujo de conversaciones y tickets de todos los negocios del SaaS.</p>
                        </div>
                    </div>
                    <div className="h-[280px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData}>
                                <defs>
                                    <linearGradient id="colorConv" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorTickets" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#475569', fontSize: 12}} />
                                <YAxis axisLine={false} tickLine={false} tick={{fill: '#475569', fontSize: 12}} />
                                <Tooltip 
                                    contentStyle={{ 
                                        backgroundColor: '#0F172A', 
                                        borderRadius: '16px', 
                                        border: '1px solid #1e293b',
                                        color: '#fff'
                                    }}
                                />
                                <Area type="monotone" dataKey="conversaciones" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorConv)" />
                                <Area type="monotone" dataKey="tickets" stroke="#8b5cf6" strokeWidth={3} fillOpacity={1} fill="url(#colorTickets)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </motion.div>

                {/* Info Cards */}
                <div className="space-y-8">
                    <motion.div 
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="p-6 rounded-[2rem] bg-[#0F172A]/40 backdrop-blur-3xl border border-slate-800/50"
                    >
                        <h2 className="text-xl font-bold text-white font-outfit mb-4">Segmentación de Mercado</h2>
                        <div className="h-[200px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={categoryData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {categoryData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                         contentStyle={{ 
                                            backgroundColor: '#0F172A', 
                                            borderRadius: '12px', 
                                            border: '1px solid #1e293b'
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="space-y-2 mt-4">
                            {categoryData.map((entry, index) => (
                                <div key={entry.name} className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                                        <span className="text-xs text-slate-400">{entry.name}</span>
                                    </div>
                                    <span className="text-xs font-bold text-white">{entry.value}%</span>
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="p-6 rounded-[2rem] bg-gradient-to-br from-blue-600/20 to-indigo-900/40 border border-blue-500/20 shadow-xl shadow-blue-900/20 relative overflow-hidden group"
                    >
                        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-150 transition-transform duration-1000">
                             <ShieldCheck className="w-24 h-24 text-white" />
                        </div>
                        <div className="relative z-10">
                            <h3 className="text-white/80 text-xs font-bold uppercase tracking-widest">Salud de Infraestructura</h3>
                            <div className="text-4xl font-black text-white mt-1">99.9%</div>
                            <div className="mt-4 w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                                <motion.div 
                                    initial={{ width: 0 }}
                                    animate={{ width: '99.9%' }}
                                    transition={{ duration: 1.5, ease: "easeOut" }}
                                    className="h-full bg-emerald-400"
                                />
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Top Tenants Table */}
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 rounded-[2rem] bg-[#0F172A]/40 backdrop-blur-3xl border border-slate-800/50"
            >
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-white font-outfit">Top Bots Activos</h2>
                    <Globe className="w-5 h-5 text-slate-500" />
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="border-b border-slate-800 text-xs font-bold text-slate-500 uppercase tracking-widest">
                                <th className="pb-4">Agente IA</th>
                                <th className="pb-4">Estado</th>
                                <th className="pb-4 text-right">Interacciones</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm text-slate-300">
                            {bots.slice(0, 5).map((bot, idx) => (
                                <tr key={idx} className="border-b border-slate-800/50 last:border-0 hover:bg-white/[0.02] transition-all group">
                                    <td className="py-4 font-bold text-white flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                            <Activity className="w-4 h-4 text-blue-500" />
                                        </div>
                                        {bot.name}
                                    </td>
                                    <td className="py-4">
                                        <span className={`px-2 py-1 ${bot.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-orange-500/10 text-orange-400 border-orange-500/20'} text-[10px] font-bold rounded-full border uppercase`}>
                                            {bot.status}
                                        </span>
                                    </td>
                                    <td className="py-4 text-right font-mono text-xs">{bot.total_conversations || 0}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </motion.div>
        </div>
    );
};

export default DashboardPage;
